import Translator from "../../utils/translator";
import { CancelablePromise as Promise } from "../../utils/cancelablePromise";
import { extend, removeElement, createElement, setCss, hashToArray } from "../../utils/vanilla";
import { isDriverViewingInspectionMode } from "../../utils/uri";
import Spinner from "../../utils/spinner";
import { DriverActionTypes } from "../../store/mobile/types";
import { DriveStore } from "../../store/reducers";
import { MobileActions } from "../../store/mobile/actions";
import { dispatcher, DispatcherEvents } from "../../utils/dispatcher";
import { focusLocker, getFocusableItems } from "../../utils/focusLocker";
import { uuid } from "geotab-uuid";

const DIALOG_MIN_MARGIN = 5;

let dialogStack: any[] = [];
let nonClosableDialogsStash: any[] = [];
let dialogsHashMap: { [id: string]: Dialog } = {};
let popped = ("state" in window.history);
let initialURL = location.href;
let interruptedDialogId: string | null = null;

interface IDialogOptions {
    modal?: boolean;
    closable?: boolean;
    expandable?: boolean;
    intervenient?: boolean; // lets popup to interrupt current unclosable dialog. After popup is closed previous dialog will proceed
    showCloseIcon?: boolean;
    focusOnFirstNonExitButton?: boolean; // Focus first non-exit button when dialog is closeable
    persistent?: boolean; // dialog will not remove when redirected
    alignToElement?: string | HTMLElement;
    isSelfClosing?: number;
    isMaxHeight?: boolean;
    buttonsText?: string;
    adjustable?: boolean;
    bindWidth?: boolean;
    minWidth?: number;
    onCancelCallback?: () => void;
    attributes?: IStringHash;
    isNightMode?: boolean | null;
    customClass?: string;
    hideTitle?: boolean;
}

interface IDialogButton {
    text: string;
    icon?: string;
    id?: string;
    className?: string;
    el?: HTMLElement;
    callback: (dialog: Dialog, e: Event) => void;
    spinner?: boolean;
    handler?: (e: Event) => void;
}

const PHRASES = {
    collapseTitle: "Collapse",
    expandTitle: "Expand",
    closeTitle: "Close"
};

class Dialog {
    currentOptions: IDialogOptions;
    el?: Element;
    private readonly title?: string;
    private readonly dialogId: string;
    private readonly overlayId: string;
    private readonly translations: { [key: string]: string } = {};
    private readonly onResizeBind;
    private readonly onRemoveBind;
    private readonly logInteraction: (e: Event) => void;
    private dialog: HTMLElement | null;
    private buttons?: IDialogButton[][] | IDialogButton[];
    private selectElements?: NodeListOf<HTMLSelectElement>;
    private message: string | HTMLElement[] | Function;
    private onOpenCallback?: () => void;
    private onRemoveCallback: () => void;
    private overlay: HTMLDivElement | null;
    private closeButton: HTMLButtonElement;
    private timer;
    private expanded = false;
    private expandButton?: HTMLElement;
    private translator: Translator;

    constructor(id: string | null, message: string | HTMLElement[], translator: Translator, title?: string, buttons?: IDialogButton[][] | IDialogButton[], options?: IDialogOptions) {
        this.currentOptions = extend({}, {
            modal: true,
            closable: true,
            showCloseIcon: true,
            focusOnFirstNonExitButton: true,
            attributes: {
                "role": "dialog"
            }
        }, options || {});
        this.currentOptions.persistent = options?.persistent ?? !this.currentOptions.closable;
        this.title = title;
        this.message = message;
        this.buttons = buttons;
        this.translator = translator;
        this.dialogId = id || `dialog-box-${ uuid() }`;
        this.overlayId = `overlay-${ this.dialogId }`;
        this.logInteraction = (e: Event) => dispatcher.trigger(DispatcherEvents.DriveAppInteraction, e);
        this.onRemoveBind = e => {
            if ((e && e.key && e.key !== "Escape") || (!e.key && e.type === "keyup")) {
                return;
            }

            this.logInteraction(e);
            this.currentOptions && this.currentOptions.onCancelCallback && this.currentOptions.onCancelCallback();
            this.remove();
        };
        this.onResizeBind = () => this.resize();
    }

    get interrupted() {
        return interruptedDialogId === this.dialogId;
    }

    get messages() {
        return this.message;
    }

    get waiting() {
        let spinner: Spinner | undefined;
        return {
            start: () => {
                if (!this.dialog) {
                    return;
                }
                let buttonRegion = this.dialog.querySelector(".dialog-box__button-region");
                this.dialog.classList.add("_in-progress");

                if (buttonRegion) {
                    let spin = buttonRegion.querySelector(".dialog-spinner");

                    if (!spin) {
                        spin = createElement("div", "dialog-spinner");
                        buttonRegion.appendChild(spin);

                        spinner = new Spinner().spin(spin);
                    }
                }
            },
            stop: () => {
                if (!this.dialog) {
                    spinner?.stop();
                    return;
                }
                let buttonRegion = this.dialog.querySelector(".dialog-box__button-region");
                this.dialog.classList.remove("_in-progress");

                if (buttonRegion) {
                    let spin = buttonRegion.querySelector(".dialog-spinner");
                    spin && spin.parentNode && spin.parentNode.removeChild(spin);

                    // Re-enable only buttons that were disabled by spinner (not business-logic-disabled buttons)
                    buttonRegion.querySelectorAll<HTMLButtonElement>("button[data-disabled-by-spinner='true']").forEach(btn => {
                        btn.disabled = false;
                        delete btn.dataset.disabledBySpinner;
                    });
                }
            }
        };
    }

    addKeyboardRemove() {
        if (this.currentOptions.closable) {
            // allow use of escape key to close dialog
            window.addEventListener("keyup", this.onRemoveBind, false);
        }
    }

    open(translator?: Translator, language?: string): Promise<Dialog> {
        if (translator) {
            this.translator = translator;
        }

        if (!this.dialog) {
            this.dialog = createElement("div", "dialog-box", this.dialogId);
        }

        let setOverlayZIndex = (overlay) => {
            if (this.currentOptions.closable) {
                overlay.style.zIndex = "7000";
                overlay.addEventListener("click", this.onRemoveBind, false);
            } else {
                overlay.style.zIndex = "9000";
            }
        };

        let setDialogZindex = () => {
            if (this.dialog) {
                this.dialog.style.zIndex = this.currentOptions.closable ? "8000" : "10000";
            }
        };

        let setButtons = () => {
            if (this.dialog && this.buttons && this.buttons.length) {
                let buttonRegion = createElement("div", "dialog-box__button-region");
                if (this.currentOptions.buttonsText) {
                    let buttonText = createElement("div", "dialog-box__button-text");
                    this.setButtonText(buttonText);
                    buttonRegion.appendChild(buttonText);
                }
                this.setButtons(buttonRegion);
                this.dialog.appendChild(buttonRegion);
            }
        };

        let addToHistory = () => {
            // Fixes an issue with navigation when going to a Zenith module
            // from the topbar. The language-selection-dialog is a lightweight popover that
            // manages its own close; pushing history causes the Drive router to treat it as
            // navigation and immediately close it.
            if (this.dialogId === "notifications-dialog" || this.dialogId === "language-selection-dialog") {
                return;
            }

            dialogStack.unshift(this.dialogId);
            let openedDialogsCount = (window.history.state ? window.history.state.openedDialogCount : null) || 0;
            openedDialogsCount++;
            if (dialogStack.length >= 1) {
                window.history.pushState(extend(window.history.state || {}, {
                    dialogStack: dialogStack,
                    openedDialogCount: openedDialogsCount
                }), document.title, location.href);
            } else {
                window.history.replaceState(extend(window.history.state || {}, {
                    dialogStack: dialogStack,
                    openedDialogCount: openedDialogsCount
                }), document.title, location.href);
            }
        };

        let renderTitle = (title?: HTMLElement[]) => {
            let titleBar = title ? createElement("div", "dialog-box__header") : undefined;
            if (titleBar && title && title.length) {
                if (this.currentOptions.hideTitle) {
                    this.dialog?.setAttribute("aria-label", title[0].textContent || "");
                } else {
                    this.setTitle(titleBar, title);
                    this.dialog?.appendChild(titleBar);
                    this.dialog?.setAttribute("aria-label", this.title as string);
                }
            }
            this.renderCloseButton(titleBar);
        };

        // eslint-disable-next-line complexity
        return this.translateDialog(language).then(([title, message]) => {
            if (!this.dialog) {
                return this;
            }
            let innerDialog = createElement("div", "dialog-box__inner-wrapper");
            innerDialog.setAttribute("tabindex", "0");
            let dialogContent = createElement("div", "dialog-box__inner", `${ this.dialogId }-inner`);

            // Check for any un-closable popups, and prevent this one from opening if there is one opened
            let dialogs = hashToArray<Dialog>(dialogsHashMap);
            let unclosableDialogIsOpen = dialogs.some(dialog => dialog.currentOptions.closable === false);
            let openedDialog = dialogStack[0] && dialogsHashMap[dialogStack[0]];
            let shouldInterruptPrevious = this.currentOptions.intervenient && openedDialog;

            if (unclosableDialogIsOpen && !shouldInterruptPrevious) {
                if (!this.currentOptions.closable && !nonClosableDialogsStash.some(dialog => dialog.dialogId === this.dialogId)) {
                    nonClosableDialogsStash.push(this);
                }
                return this;
            }

            // Clear any old instance of this dialog
            this.cleanup();

            if (shouldInterruptPrevious) {
                (openedDialog as Dialog).interrupt();
            }

            if (this.currentOptions.modal) {
                this.overlay = createElement("div", "dialog-box-overlay", this.overlayId);
                this.dialog.setAttribute("aria-modal", "true");
                setOverlayZIndex(this.overlay);
            }

            setDialogZindex();

            let content = document.getElementById("content");
            if (content) {
                setCss(content, { overflow: "hidden" });
            }

            renderTitle(title);

            this.appendMessage(dialogContent, message);
            innerDialog.appendChild(dialogContent);
            this.dialog.appendChild(innerDialog);

            this.selectElements = this.dialog.querySelectorAll("select");
            if (this.selectElements && this.selectElements.length > 0) {
                Array.prototype.filter.call(this.selectElements, selectElement => selectElement.addEventListener("change", this.logInteraction));
            }

            if (this.currentOptions.expandable) {
                this.expandButton = createElement("button", "dialog-box__expand-button", "dialog-box__expand-button");
                this.updateExpandButton();
                this.dialog.classList.add("_expandable");
                this.dialog.appendChild(this.expandButton);
                this.expandButton.addEventListener("click", this.onExpandClick);
            }

            // Remove old instances of this overlay
            removeElement(this.overlay);

            // Add overlad and dialog to DOM, adjust size, and show
            setCss(this.dialog, {
                position: "absolute",
                top: 0,
                left: 0,
                opacity: 0
            });

            setButtons();

            let app = document.querySelector("div#app");
            let blockNightMode = isDriverViewingInspectionMode();

            if ((this.currentOptions.isNightMode || window.whitelabel === "lytx") && app && !blockNightMode) {
                this.dialog.classList.add("nightMode");
            }

            if (this.currentOptions.customClass) {
                this.dialog.classList.add(this.currentOptions.customClass);
            }

            this.overlay && document.body.appendChild(this.overlay);
            document.body.appendChild(this.dialog);

            dialogsHashMap[this.dialogId] = this;

            !!window.history.pushState && addToHistory();

            this.resize();
            window.addEventListener("resize", this.onResizeBind, false);

            this.addKeyboardRemove();

            let dialogInDom = <HTMLElement>document.getElementById(this.dialogId);

            setCss(dialogInDom, { opacity: 1 });
            this.alignDialog(dialogInDom);

            this.el = this.dialog;
            this.onOpenCallback?.();

            // If this is set, start a timer...
            if (this.currentOptions.isSelfClosing && this.currentOptions.isSelfClosing > 0) {
                this.timer = setTimeout(() => {
                    this.remove();
                }, this.currentOptions.isSelfClosing * 1000);
            }

            // Override default attributes by attributes from options
            let attributes = this.currentOptions.attributes;
            if (attributes) {
                for (let attributeName in attributes) {
                    this.dialog.setAttribute(attributeName, attributes[attributeName]);
                }
            }

            this.currentOptions.modal && focusLocker.lock(this.dialog);

            // Focus the first non-exit button element if dialog is closable
            if (this.currentOptions.showCloseIcon && this.currentOptions.focusOnFirstNonExitButton) {
                let focusableItems = getFocusableItems(this.dialog);
                for (let i = 0; i < focusableItems.length; i++) {
                    let item = focusableItems[i];
                    if (item.className.indexOf("dialog-box__close-button") < 0) {
                        (item as HTMLElement).focus();
                        break;
                    }
                }
            }

            return this;
        });
    }

    onRemove(callback: () => void): Dialog {
        this.onRemoveCallback = callback;
        return this;
    }

    onOpen(callback: () => void): Dialog {
        this.onOpenCallback = callback;
        return this;
    }

    cleanup(): void {
        let stackIndex = dialogStack.indexOf(this.dialogId);

        if (stackIndex !== -1) {
            dialogStack.splice(stackIndex, 1);
            if (window.history.replaceState) {
                window.history.replaceState(extend(window.history.state || {}, { dialogStack }), document.title, location.href);
            }
        }

        this.currentOptions.modal && focusLocker.unlock();
        let dialogToRemove = document.getElementById(this.dialogId);
        removeElement(dialogToRemove);
        let content = document.getElementById("content");
        content && content.removeAttribute("style");
        let overlayToRemove = document.getElementById(this.overlayId);
        removeElement(overlayToRemove);

        this.onRemoveCallback && this.onRemoveCallback();

        window.removeEventListener("resize", this.onResizeBind, false);
        if (this.currentOptions.closable) {
            // allow use of escape key to close dialog
            window.removeEventListener("keyup", this.onRemoveBind, false);
        }

        this.expandButton && this.expandButton.removeEventListener("click", this.onExpandClick, false);
        this.timer && clearTimeout(this.timer);

        if (!this.currentOptions.closable) {
            nonClosableDialogsStash = nonClosableDialogsStash.filter(dialog => dialog.dialogId !== this.dialogId);
            nonClosableDialogsStash.length && nonClosableDialogsStash.shift().open();
        }

        if (interruptedDialogId === this.dialogId) {
            interruptedDialogId = null;
        }

        if (this.currentOptions.intervenient && interruptedDialogId) {
            dialogsHashMap[interruptedDialogId].proceed();
        }

        delete dialogsHashMap[this.dialogId];
    }

    remove() {
        this.cleanup();
        this.dialog = null;
        if (this.buttons && Array.isArray(this.buttons[0])) {
            this.buttons.forEach(buttonRow => {
                buttonRow.forEach(thisButton => {
                    thisButton.handler && thisButton.el && (thisButton.el as HTMLButtonElement).removeEventListener("click", thisButton.handler, false);
                    thisButton.callback && delete thisButton.callback;
                });
            });
        }

        if (this.selectElements && this.selectElements.length > 0) {
            Array.prototype.filter.call(this.selectElements, selectElement => selectElement.removeEventListener("change", this.logInteraction));
        }
        delete this.selectElements;

        this.overlay && this.overlay.removeEventListener("click", this.onRemoveBind, false);
        this.closeButton && this.closeButton.removeEventListener("click", this.onRemoveBind, false);
        delete this.buttons;
    }

    calculateWidth(dialogWidth): number {
        let windowWidthIncMargin = window.innerWidth - DIALOG_MIN_MARGIN * 2;
        let calculatedWidth = dialogWidth > windowWidthIncMargin || this.expanded ? windowWidthIncMargin : dialogWidth;
        let minWidth = this.currentOptions.minWidth;
        if (minWidth && calculatedWidth < minWidth) {
            return minWidth < windowWidthIncMargin ? minWidth : windowWidthIncMargin;
        }
        return calculatedWidth;
    }

    resize(): void {
        let dialogBox = this.dialog;
        let dialogContent: HTMLElement | null = this.dialog?.querySelector(".dialog-box__inner") || null;
        let wHeight = window.innerHeight;
        let maxHeight = `${ wHeight - DIALOG_MIN_MARGIN * 2 }px`;

        if (dialogBox && dialogContent) {
            dialogBox.style.height = this.currentOptions.isMaxHeight ? maxHeight : "auto";
            dialogBox.style.width = "auto";
            dialogContent.style.height = "auto";
            dialogContent.style.width = "auto";

            let dimensions = dialogBox.getBoundingClientRect();
            let dWidth = dimensions.width;

            dialogBox.style.maxHeight = maxHeight;
            let alignToElement = this.getAlignToElement();
            dialogBox.style.width = this.currentOptions.bindWidth && this.currentOptions.alignToElement && alignToElement
                ? `${ alignToElement.offsetWidth }px`
                : `${ this.calculateWidth(dWidth) }px`;

            this.alignDialog(dialogBox);
        } else {
            window.removeEventListener("resize", this.onResizeBind, false);
        }
    }

    setMessage(newMessage: HTMLElement[] | string) {
        this.message = newMessage;
        if (!this.dialog) {
            return;
        }
        let dialogContent: HTMLElement | null = this.dialog.querySelector(".dialog-box__inner");
        if (dialogContent) {
            dialogContent.innerHTML = "";
            dialogContent && this.appendMessage(dialogContent, newMessage);
        }
    }

    setLanguage(language: string) {
        return this.translateDialog(language).then(([title, message]) => {
            let titleBar: HTMLElement | undefined = this.dialog?.querySelector(".dialog-box__header") || undefined;
            if (titleBar && title && title.length) {
                titleBar.innerHTML = "";
                this.setTitle(titleBar, title);
            }
            this.setMessage(message);
            this.renderCloseButton(titleBar);
            this.updateExpandButton();
            let buttonRegion: HTMLElement | undefined = this.dialog?.querySelector(".dialog-box__button-region") || undefined;
            if (buttonRegion && this.buttons && this.buttons.length) {
                let buttonText = this.dialog?.querySelector(".dialog-box__button-text");
                let buttonRows = this.dialog?.querySelectorAll(".dialog-box__button-row");

                if (buttonText) {
                    this.setButtonText(buttonText as HTMLElement);
                }
                [].forEach.call(buttonRows, buttonRow => (<HTMLElement>buttonRegion).removeChild(buttonRow));
                this.setButtons(buttonRegion);
            }
        });
    }

    getTitle() {
        return this.el?.querySelector(".dialog-box__title")?.textContent;
    }

    isOpen() {
        return !!dialogsHashMap[this.dialogId];
    }

    private appendMessage(wrapper: HTMLElement, message: HTMLElement[] | HTMLElement | string) {
        if (wrapper) {
            if (message instanceof String || typeof (message) === "string") {
                wrapper.innerHTML = message as string;
            } else {
                if (Array.isArray(message)) {
                    [].forEach.call(message, (htmlElem) => {
                        wrapper.appendChild(htmlElem);
                    });
                } else {
                    wrapper.appendChild(message);
                }
            }
        }
    }

    private setTitle(wrapper: HTMLElement, title: HTMLElement[]) {
        wrapper.appendChild(title[0]);
        this.dialog?.classList.add("_titled");
    }

    private setButtonText(container: HTMLElement) {
        container.innerHTML = this.translations[(<string> this.currentOptions.buttonsText)];
    }

    private getAlignToElement() {
        return typeof this.currentOptions.alignToElement === "string" ? document.querySelector<HTMLElement>(this.currentOptions.alignToElement) : this.currentOptions.alignToElement || null;
    }

    private alignDialog(dialogEl: HTMLElement): HTMLElement {
        let el = this.getAlignToElement();

        if (el) {
            let dimRect = el.getBoundingClientRect();

            let ccsTop: number;
            let cssLeft: number;
            let maxHeight: number;

            let isNotEnoughSpace = () => {
                let dialogHeight = dialogEl.offsetHeight;
                let spaceBelow = window.innerHeight - dimRect.bottom;
                let isEnough = dialogHeight <= spaceBelow;
                let spaceAbove = dimRect.top;
                let isMoreSpaceOnTop = spaceBelow < spaceAbove;

                return this.currentOptions.adjustable && !isEnough && isMoreSpaceOnTop;
            };

            if (isNotEnoughSpace()) {
                ccsTop = dimRect.top - dialogEl.offsetHeight;
                cssLeft = dimRect.left;
                maxHeight = dimRect.top;
            } else {
                ccsTop = el.offsetHeight + dimRect.top;
                cssLeft = dimRect.left + el.offsetWidth - dialogEl.offsetWidth;
                maxHeight = window.innerHeight - dimRect.bottom;
            }

            setCss(dialogEl, {
                top: `${ ccsTop > DIALOG_MIN_MARGIN ? ccsTop : DIALOG_MIN_MARGIN }px`,
                left: `${ cssLeft > DIALOG_MIN_MARGIN ? cssLeft : DIALOG_MIN_MARGIN }px`,
                maxHeight: `${ maxHeight }px`
            });
        } else {
            setCss(dialogEl, {
                top: `${ (window.innerHeight - dialogEl.offsetHeight) / 2 }px`,
                left: `${ (window.innerWidth - dialogEl.offsetWidth) / 2 }px`
            });
        }

        return dialogEl;
    }

    private updateExpandButton() {
        if (this.expandButton) {
            this.expandButton.setAttribute("aria-label", this.expanded ? this.translations[PHRASES.collapseTitle] : this.translations[PHRASES.expandTitle]);
            if (this.expanded) {
                this.expandButton.innerHTML = "<svg class='svgIcon bigger' role='img' aria-label='Minimize icon'><use xlink:href='#drive-resize-small'></use></svg>";
            } else {
                this.expandButton.innerHTML = "<svg class='svgIcon bigger' role='img' aria-label='Maximize icon'><use xlink:href='#drive-resize-full'></use></svg>";
            }
        }
    }

    private readonly onExpandClick = e => {
        this.logInteraction(e);
        this.expanded = !this.expanded;
        this.updateExpandButton();
        this.expanded ? this.dialog?.classList.add("_expanded") : this.dialog?.classList.remove("_expanded");
        this.resize();
    };

    private translatePhrases(language?: string) {
        let phrases = Object.keys(PHRASES).map(item => PHRASES[item]);
        if (this.buttons && this.buttons.length) {
            let buttons = (<IDialogButton[]> this.buttons[0]).length > 0 ? <IDialogButton[][]> this.buttons : new Array<IDialogButton[]>(<IDialogButton[]> this.buttons);

            buttons.forEach(buttonRow => {
                buttonRow.forEach(thisButton => {
                    phrases.push(thisButton.text);
                });
            });
        }
        this.currentOptions.buttonsText && phrases.push(this.currentOptions.buttonsText);
        return this.translator.translate(phrases, language)
            .then(translated => {
                phrases.forEach((phrase, i) => {
                    this.translations[phrase] = translated[i];
                });
            });
    }

    private translateDialog(language?: string): Promise<[HTMLElement[] | undefined, HTMLElement[], void]> {
        return Promise.all([
            this.title
                ? this.translator.translateTemplate(`<span id="${ this.dialogId }-title" class='dialog-box__title heading-3'>${ this.title }</span>`, language)
                : Promise.resolve(undefined as any),

            (typeof this.message === "string" || typeof this.message === "function")
                ? this.translator.translateTemplate(this.message, language)
                : this.translator.translateHtml(this.message, language),
            this.translatePhrases(language)
        ]);
    }

    private renderCloseButton(container?: HTMLElement) {
        if (!this.dialog) {
            return;
        }
        if (this.currentOptions.closable && this.currentOptions.showCloseIcon) {
            let closeButton = createElement("button", "dialog-box__close-button", `closeButton-${ this.dialogId }`);
            this.dialog.classList.add("_closable");

            closeButton.innerHTML = "<svg class='svgIcon big icon-cancel' role='img' aria-label='Cancel icon'><use xlink:href='#drive-cancel'></use></svg>";
            closeButton.setAttribute("aria-label", this.translations[PHRASES.closeTitle]);
            closeButton.addEventListener("click", this.onRemoveBind, false);

            if (container) {
                container.appendChild(closeButton);
            } else {
                closeButton.classList.add("dialog-box__close-button_floating");
                this.dialog.appendChild(closeButton);
            }
            this.closeButton = closeButton;
        }
    }

    private setButtons(container: HTMLElement) {
        let dialogButtons = this.buttons as IDialogButton[] | IDialogButton[][];
        let buttons = (<IDialogButton[]>dialogButtons[0]).length > 0 ? <IDialogButton[][]>dialogButtons : new Array<IDialogButton[]>(<IDialogButton[]>dialogButtons);

        buttons.forEach(buttonRow => {
            let buttonRowDiv = createElement("div", "dialog-box__button-row");

            buttonRow.forEach(thisButton => {
                let button: HTMLElement | null;

                button = createElement("button", "dialog-box__button", thisButton.id || `button-${ uuid() }`);
                button.innerHTML = (thisButton.icon ? `<svg class="svgIcon" role="img" aria-label="Button icon"><use xlink:href="#${ thisButton.icon }"></use></svg> ` : "") +
                    this.translations[thisButton.text];

                if (thisButton.className) {
                    button.classList.add(thisButton.className);
                }

                // Bind event callback;
                thisButton.handler = e => {
                    // BQ log that we're clicking this button
                    this.logInteraction(e);

                    if (thisButton.spinner) {
                        // Prevent double-tap: disable button immediately on first click
                        const buttonEl = e.currentTarget as HTMLButtonElement;
                        if (buttonEl.disabled) {
                            return;
                        }
                        buttonEl.disabled = true;
                        buttonEl.dataset.disabledBySpinner = "true";

                        this.waiting.start();
                        // JS is too busy before spinner can be loaded causing a frozen screen,
                        // adding timeout solves this issue
                        const callback = thisButton.callback;
                        setTimeout(() => {
                            callback(this, e);
                        }, 0);
                        return;
                    }

                    thisButton.callback(this, e);
                };
                button.addEventListener("click", thisButton.handler, false);
                buttonRowDiv.appendChild(button);
                thisButton.el = button;
            });
            container.appendChild(buttonRowDiv);
        });

        this.buttons = buttons;
    }

    private interrupt() {
        this.dialog?.classList.add("_hidden");
        this.overlay?.classList.add("_hidden");
        interruptedDialogId = this.dialogId;
    }

    private proceed() {
        this.dialog?.classList.remove("_hidden");
        this.overlay?.classList.remove("_hidden");
        interruptedDialogId = null;
    }
}

window.addEventListener("popstate", () => {
    // Workaround for pop on page load in Chrome
    // from: https://github.com/defunkt/jquery-pjax/blob/master/jquery.pjax.js
    let initialPop = !popped && location.href === initialURL;
    popped = true;
    if (initialPop) {
        return;
    }

    if (dialogStack.length) {
        let dialogid = dialogStack[0];
        let dialogToClose = dialogsHashMap[dialogid];

        if (dialogToClose && !dialogToClose.currentOptions.persistent) {
            dialogToClose.remove();
        }

        if (window.history.pushState) {
            window.history.pushState(extend(window.history.state || {}, {
                dialogStack: dialogStack
            }), document.title, location.href);
        }
    }
});

let removeAllDialogs = () => {
    let dialogsIds = Object.keys(dialogsHashMap);
    nonClosableDialogsStash = [];

    dialogsIds.forEach(id => {
        dialogsHashMap[id].remove();
    });

    dialogsHashMap = {};

    return dialogsIds.length;
};

let removeDialog = (dialogId: string): number => {
    let dialog = dialogsHashMap[dialogId];
    const stashedDialogCount = nonClosableDialogsStash.length;
    nonClosableDialogsStash = nonClosableDialogsStash.filter(stashedDialog => stashedDialog.dialogId !== dialogId);
    dialog && dialog.remove();
    delete dialogsHashMap[dialogId];

    return dialog || stashedDialogCount !== nonClosableDialogsStash.length ? 1 : 0;
};

// Interface for dialog-like objects that can work with applyDriverActionNecessary
interface IRemovable {
    onRemove: (callback: () => void) => any;
}

let applyDriverActionNecessary = <T extends IRemovable>(store: DriveStore, type: DriverActionTypes, onRemove?: () => void) => {
    return function (dialog: T): T {
        dialog.onRemove(() => {
            store.dispatch(MobileActions.setDriverActionNecessary({ isNecessary: false, actionType: type }));
            onRemove?.();
        });

        store.dispatch(MobileActions.setDriverActionNecessary({ isNecessary: true, actionType: type }));
        return dialog;
    };
};

let offsetArrowUp = (buttonEl: HTMLElement | null, dialogId: string) => {
    let dialog: HTMLElement | null = document.getElementById(dialogId);
    let arrowUp: HTMLElement | null = document.getElementById("arrow-up");
    let buttonRectangle = buttonEl?.getBoundingClientRect();
    let dialogRectangle = dialog?.getBoundingClientRect();

    if (arrowUp && buttonRectangle && dialogRectangle) {
        let offset = dialogRectangle.right - buttonRectangle.right + (buttonRectangle.width - 20) / 2;
        arrowUp.style.right = `${ offset }px`;
    }
};

export { Dialog, IDialogButton, IDialogOptions, removeAllDialogs, removeDialog, applyDriverActionNecessary, offsetArrowUp };
